import { useEffect, useRef, useState } from 'react';
import { Button } from '@/marketing/components/ui/button';
import type { ScoutingCounts } from '@/marketing/models/campaignScouting';
import { extractErrorMessage } from '@/marketing/lib/errorMessage';
import { fetchScoutingExportRows } from './scoutingExportPages';
import {
  downloadScoutingWorkbook,
  SCOUTING_EXPORT_SCOPES,
  type ScoutingExportScope,
  type ScoutingExportAudience,
} from './scoutingExport';

export function ScoutingExports({
  campaignId,
  counts,
  disabled,
}: {
  campaignId: string;
  counts?: ScoutingCounts | undefined;
  disabled: boolean;
}) {
  const [scope, setScope] = useState<ScoutingExportScope>('finalistsAndSelected');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const active = useRef<AbortController | null>(null);
  useEffect(() => () => active.current?.abort(), [campaignId]);
  const count = counts
    ? scope === 'selected'
      ? counts.Selected
      : scope === 'finalists'
        ? counts.Finalist
        : counts.Finalist + counts.Selected
    : undefined;
  const download = async (audience: ScoutingExportAudience) => {
    if (active.current) {
      return;
    }
    const controller = new AbortController();
    active.current = controller;
    setPending(true);
    setError(undefined);
    try {
      const rows = await fetchScoutingExportRows(campaignId, scope, controller.signal);
      if (!controller.signal.aborted) {
        downloadScoutingWorkbook(rows, audience, scope);
      }
    } catch (failure) {
      if (!controller.signal.aborted) {
        setError(extractErrorMessage(failure, 'Could not export scouting.'));
      }
    } finally {
      if (active.current === controller) {
        active.current = null;
      }
      setPending(false);
    }
  };
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs">
      <label>
        Export scope{' '}
        <select
          aria-label="Export scope"
          className="tw-rounded tw-border tw-bg-background tw-p-1.5"
          value={scope}
          disabled={pending}
          onChange={(event) => setScope(event.target.value as ScoutingExportScope)}
        >
          {Object.entries(SCOUTING_EXPORT_SCOPES).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || pending || !count}
        onClick={() => void download('internal')}
      >
        Export internal ({count ?? '…'})
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || pending || !count}
        onClick={() => void download('brand')}
      >
        Export brand ({count ?? '…'})
      </Button>
      {pending && (
        <Button size="sm" variant="outline" onClick={() => active.current?.abort()}>
          Cancel export
        </Button>
      )}
      <span className="tw-text-muted-foreground">
        XLSX · All campaign rows in scope, regardless of table filters or page. Declined/rejected excluded. Hidden
        form/check history is omitted. Brand file excludes private review/contact data.
      </span>
      {pending && <span role="status">Preparing full export…</span>}
      {error && (
        <span role="alert" className="tw-text-red-700">
          {error}
        </span>
      )}
    </div>
  );
}
